import { ConfirmationModal, type ConfirmationModalProps } from '@shared/components';
import { useCallback, useEffect, useRef } from 'react';
import { useBlocker, type BlockerFunction, type Location } from 'react-router-dom';

export interface NavigationBlockerProps {
  slotProps?: {
    confirmationModal?: Omit<
      Partial<ConfirmationModalProps>,
      'open' | 'acceptText' | 'discardText' | 'onAccept' | 'onClose' | 'onDiscard'
    > & {
      confirmExitText?: string;
      cancelText?: string;
    };
  };
  shouldBlock?: boolean | ((currentLocation: Location, nextLocation: Location) => boolean);
}

/**
 * NavigationBlocker is a React component that listens for page navigation events
 * and prompts the user with a confirmation modal if they attempt to leave the page.
 *
 * @param {Readonly<NavigationBlockerProps>} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child components to be rendered within this component.
 * @param {boolean | ((currentLocation: Location, nextLocation: Location) => boolean)} props.shouldBlock - A boolean value or a function that returns a boolean value to determine if the navigation should be blocked.
 * @param {string} props.slotProps.confirmModal.title - The title of the confirmation modal.
 * @param {string} props.slotProps.confirmModal.content - The content/message of the confirmation modal.
 * @param {string} props.slotProps.confirmModal.confirmExitText - The text for the exit button in the confirmation modal.
 * @param {string} props.slotProps.confirmModal.cancelText - The text for the cancel button in the confirmation modal.
 * @param {object} props.slotProps.confirmModal.restConfirmModalProps - Additional properties for the confirmation modal.
 *
 * @remarks
 * This component uses the `useBlocker` hook from `react-router` to block navigation and display a confirmation modal.
 * It also uses a workaround to check if the component is mounted or not due to an issue with `react-router`.
 *
 * @see https://github.com/remix-run/react-router/issues/11430#issuecomment-2345829613
 *
 * @example
 *
 * This component can be used as a standalone component which will return the confirmation modal when the user tries to navigate away from the page.
 * `shouldBlock` is set to `true` by default.
 * ```tsx
 * <NavigationBlocker />
 * ```
 *
 * The confirmation modal can be customized by passing additional properties to the `slotProps` prop.
 * ```tsx
 * <NavigationBlocker
 *   slotProps={{
 *     confirmModal: {
 *       title: 'Confirm Navigation',
 *       content: 'Are you sure you want to leave this page?',
 *       confirmExitText: 'Leave',
 *       cancelText: 'Stay',
 *     },
 *   }}
 * />
 * ```
 *
 * since the `shouldBlock`'s default value is `true`, the navigation will be blocked by default.
 * To disable the navigation blocker, you can pass `false` to the `shouldBlock` prop.
 * or adjust the logic in the `shouldBlock` function to return `false`.
 *
 * For usage with custom logic to block navigation, you can pass a function to the `shouldBlock` prop.
 * ```tsx
 * <NavigationBlocker
 *  shouldBlock={(currentLocation, nextLocation) => {
 *   // Custom logic to block navigation
 *  return currentLocation === '/home' && nextLocation === '/dashboard';
 * }}
 * />
 * ```
 */

export default function NavigationBlocker(props: Readonly<NavigationBlockerProps>) {
  const { slotProps, shouldBlock = true } = props;
  const { confirmationModal } = slotProps ?? {};
  const { title, content, confirmExitText, cancelText, ...restConfirmModalProps } =
    confirmationModal ?? {};

  // NOTE: This is just a workaround for blocker
  // Issue here: https://github.com/remix-run/react-router/issues/11430#issuecomment-2345829613
  // useRef to check if the component is mounted or not
  const isMountedRef = useRef<boolean>();
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const blockerExecution = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      const shouldBlockNavigation =
        typeof shouldBlock === 'function'
          ? shouldBlock(currentLocation, nextLocation)
          : shouldBlock;
      return (
        !!isMountedRef.current &&
        currentLocation.pathname !== nextLocation.pathname &&
        shouldBlockNavigation
      );
    },
    [shouldBlock, isMountedRef],
  );

  const blocker = useBlocker(blockerExecution);

  const isBlocked = blocker.state === 'blocked';

  const continueToNextPage = () => {
    blocker.proceed?.();
  };

  const cancelNavigation = () => {
    blocker.reset?.();
  };

  return (
    <ConfirmationModal
      open={isBlocked}
      title={title ?? 'Exit this page?'}
      content={content ?? 'คุณแน่ใจหรือไม่ที่จะออกจากหน้านี้'}
      onClose={cancelNavigation}
      acceptText={cancelText ?? 'Stay on Page'}
      onAccept={cancelNavigation}
      discardText={confirmExitText ?? 'Exit'}
      onDiscard={continueToNextPage}
      {...restConfirmModalProps}
    />
  );
}
